import { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash, DotsThree, Eye, ArrowSquareOut, DownloadSimple } from '@phosphor-icons/react';
import { Link as RouterLink } from 'react-router-dom';
import { adminFunctions } from '../../firebase/adminFunctions';
// Firebase Storage no longer needed - using R2
import ImagePreviewModal from '../ImagePreviewModal';
import {
  Table,
  Tbody,
  Thead,
  Tr,
  Th,
  Td,
  Button,
  Text,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Badge,
  IconButton,
  Box,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  Image,
  FormControl,
  FormLabel,
  Textarea,
} from '@chakra-ui/react';

export default function MediaManager() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMedia, setEditingMedia] = useState(null);
  const [viewingMedia, setViewingMedia] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();

  // Use useCallback to prevent unnecessary rerenders
  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const mediaData = await adminFunctions.getAllMedia();
      setMedia(mediaData);
    } catch (error) {
      toast({
        title: 'Error loading media',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load media on component mount
  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const handleUpdateMedia = async (updates) => {
    setLoading(true);
    try {
      await adminFunctions.updateMedia(editingMedia.id, updates);
      await loadMedia(); // Refresh media list
      setEditingMedia(null);
      toast({
        title: 'Media updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating media',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Are you sure you want to delete this media? This action cannot be undone.')) {
      return;
    }

    setLoading(true);
    try {
      await adminFunctions.deleteMedia(mediaId);
      await loadMedia(); // Refresh media list
      toast({
        title: 'Media deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting media',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredMedia = media.filter(item =>
    item.filename?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.uploadedByName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.site?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Box>
      <VStack spacing={4}>
        <Input
          placeholder="Search media..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Box overflowX="auto" w="100%">
          {loading ? (
            <Box textAlign="center" py={8}>
              <Spinner />
            </Box>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Preview</Th>
                  <Th>Details</Th>
                  <Th>Site</Th>
                  <Th>Uploaded By</Th>
                  <Th>Date</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredMedia.map((item) => (
                  <Tr key={item.id}>
                    <Td width="100px">
                      {item.url && (
                        <Image
                          src={item.url}
                          alt={item.filename}
                          boxSize="80px"
                          objectFit="cover"
                          borderRadius="md"
                          onClick={() => setViewingMedia(item)}
                          cursor="pointer"
                        />
                      )}
                    </Td>
                    <Td>
                      <VStack align="start" spacing={2}>
                        <Text fontWeight="medium" noOfLines={1}>
                          {item.filename}
                        </Text>
                        {item.description && (
                          <Text fontSize={{ base: 'xs', md: 'sm' }} color="text.muted" noOfLines={2}> {/* Responsive font + theme color */}
                            {item.description}
                          </Text>
                        )}
                        <Badge>
                          {item.type || 'unknown'}
                        </Badge>
                      </VStack>
                    </Td>
                    <Td>
                      {item.site ? (
                        <Box>
                          <RouterLink to={`/site/${item.siteId}`}>
                            <HStack color="text.link" spacing={1}> {/* Use theme link color */}
                              <Text>{item.site.name}</Text>
                              <ArrowSquareOut size={16} />
                            </HStack>
                          </RouterLink>
                        </Box>
                      ) : (
                        <Text color="text.muted">N/A</Text> /* Use theme muted color */
                      )} {/* Fixed ternary syntax */}
                    </Td>
                    <Td>
                      <Box>
                        <VStack align="start" spacing={1}>
                          <Text>{item.uploadedByName}</Text>
                          <Text fontSize={{ base: 'xs', md: 'sm' }} color="text.muted"> {/* Responsive font + theme color */}
                            {item.uploadedById}
                          </Text>
                        </VStack>
                      </Box>
                    </Td>
                    <Td>
                      <Box>
                        <VStack align="start" spacing={1}>
                          <Text>
                            {item.uploadedAt instanceof Date ? 
                              item.uploadedAt.toLocaleDateString() :
                              new Date(item.uploadedAt).toLocaleDateString()
                            }
                          </Text>
                          <Text fontSize={{ base: 'xs', md: 'sm' }} color="text.muted"> {/* Responsive font + theme color */}
                            {item.uploadedAt instanceof Date ?
                              item.uploadedAt.toLocaleTimeString() :
                              new Date(item.uploadedAt).toLocaleTimeString()
                            }
                          </Text>
                        </VStack>
                      </Box>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={IconButton}
                          aria-label='Options'
                          icon={<DotsThree size={20} />}
                          variant="ghost"
                          size="sm"
                        />
                        <MenuList>
                          <MenuItem 
                            icon={<Eye size={16} />}
                            onClick={() => setViewingMedia(item)}
                          >
                            View
                          </MenuItem>
                          <MenuItem
                            icon={<DownloadSimple size={16} />}
                            onClick={async () => {
                              try {
                                // Direct download from R2 public URL
                                const link = document.createElement('a');
                                link.href = item.url;
                                link.setAttribute('download', item.filename || 'image');
                                link.setAttribute('target', '_blank');
                                link.style.display = 'none';
                                document.body.appendChild(link);
                                
                                // Trigger download and cleanup
                                link.click();
                                document.body.removeChild(link);
                                
                                toast({
                                  title: 'Download started',
                                  description: `Downloading ${item.filename || 'image'}`,
                                  status: 'success',
                                  duration: 3000,
                                  isClosable: true,
                                });
                              } catch (error) {
                                console.error('Download error:', error);
                                toast({
                                  title: 'Error downloading image',
                                  description: error.message,
                                  status: 'error',
                                  duration: 5000,
                                  isClosable: true,
                                });
                              }
                            }}
                          >
                            Download
                          </MenuItem>
                          <MenuItem 
                            icon={<Pencil size={16} />}
                            onClick={() => setEditingMedia(item)}
                          >
                            Edit
                          </MenuItem>
                          <MenuItem
                            icon={<Trash size={16} />}
                            color="error" // Use theme error color
                            onClick={() => handleDeleteMedia(item.id)}
                          >
                            Delete
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      {/* View Modal */}
      {viewingMedia && (
        <ImagePreviewModal
          isOpen={!!viewingMedia}
          onClose={() => setViewingMedia(null)}
          imageUrl={viewingMedia.url}
          currentImage={viewingMedia}
        />
      )}

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingMedia}
        onClose={() => setEditingMedia(null)}
        size="xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Media</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {editingMedia && (
              <VStack spacing={4}>
                <FormControl>
                  <FormLabel>Filename</FormLabel>
                  <Input
                    value={editingMedia.filename}
                    onChange={(e) => setEditingMedia({...editingMedia, filename: e.target.value})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={editingMedia.description || ''}
                    onChange={(e) => setEditingMedia({...editingMedia, description: e.target.value})}
                    minH="120px"
                  />
                </FormControl>

                {editingMedia.url && (
                  <Image
                    src={editingMedia.url}
                    alt={editingMedia.filename}
                    maxH="200px"
                    objectFit="contain"
                  />
                )}

                <VStack align="start" spacing={0} width="100%">
                  <Badge colorScheme="gray">
                    Uploaded: {
                      editingMedia.uploadedAt instanceof Date ?
                        editingMedia.uploadedAt.toLocaleString() :
                        new Date(editingMedia.uploadedAt).toLocaleString()
                    }
                  </Badge>
                  {editingMedia.updatedAt && (
                    <Badge colorScheme="gray">
                      Updated: {
                        editingMedia.updatedAt instanceof Date ?
                          editingMedia.updatedAt.toLocaleString() :
                          new Date(editingMedia.updatedAt).toLocaleString()
                      }
                    </Badge>
                  )}
                </VStack>

                <HStack justify="flex-end" w="100%">
                  <Button variant="ghost" onClick={() => setEditingMedia(null)}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="brand.primary" // Use theme scheme
                    onClick={() => handleUpdateMedia({
                      filename: editingMedia.filename,
                      description: editingMedia.description
                    })}
                  >
                    Save Changes
                  </Button>
                </HStack>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
